import type { Invoice } from "$global-providers/rxdb-provider/collections/invoice";
import { InvoiceStatus } from "elephant-web/gql";
import type { ExternalReference } from "./build-line-item";

export type PaymentStatus = "awaiting" | "paid" | "exempt";

// Lower index wins when an order's item appears on more than one invoice.
// `awaiting` is deliberately first so an unpaid invoice always closes the
// service gate even if an older paid/void invoice carries the same item.
const STATUS_PRIORITY: readonly PaymentStatus[] = [
  "awaiting",
  "paid",
  "exempt",
];

const mapInvoiceStatus = (status: InvoiceStatus): PaymentStatus | null => {
  switch (status) {
    case InvoiceStatus.Pending:
    case InvoiceStatus.Unpaid:
      return "awaiting";
    case InvoiceStatus.Paid:
    case InvoiceStatus.PartiallyPaid:
      return "paid";
    case InvoiceStatus.Void:
      // A voided invoice exempts its orders so service delivery is never
      // permanently blocked.
      return "exempt";
    default:
      return null;
  }
};

const lineItemMatchesOrder = (
  lineItem: Invoice["lineItems"][number],
  { origin, foreignId }: ExternalReference,
): boolean =>
  lineItem.externalReference?.origin === origin &&
  lineItem.externalReference?.foreignId === foreignId;

/**
 * Derives an order's payment status by projecting the patient's replicated
 * invoices onto the order. Returns null when no invoice carries the order's
 * priced item — i.e. there is no payment gate to show (e.g. POST_SERVICE, or
 * the line item hasn't been generated yet).
 */
export const derivePaymentStatus = (
  invoices: readonly Invoice[],
  externalReference: ExternalReference,
): PaymentStatus | null => {
  let result: PaymentStatus | null = null;
  for (const invoice of invoices) {
    if (
      !invoice.lineItems.some((li) =>
        lineItemMatchesOrder(li, externalReference),
      )
    ) {
      continue;
    }
    const status = mapInvoiceStatus(invoice.invoiceStatus);
    if (status === null) continue;
    if (
      result === null ||
      STATUS_PRIORITY.indexOf(status) < STATUS_PRIORITY.indexOf(result)
    ) {
      result = status;
    }
  }
  return result;
};

/** Whether a service action (dispense / record result) may proceed. */
export const isServiceUnlocked = (status: PaymentStatus | null): boolean =>
  status === "paid" || status === "exempt";
